import {
  createElement,
  type ComponentProps,
  type ComponentType,
  type ReactElement,
} from "react";

/**
 * Wrapper around React.lazy that recovers from "stale chunk" errors
 * after a deploy.
 *
 * The problem this solves
 * -----------------------
 * A user opens the site on build A. Their HTML references
 * `assets/ServicePage-abc123.js`. We ship build B; the chunk hashes
 * change. The user keeps the tab open, then navigates to a
 * lazy-loaded route. React.lazy() fires `import()`. The browser
 * fetches `ServicePage-abc123.js`, hits a 404 in R2/CF Pages, and
 * the Worker's SPA fallback returns `index.html` instead — status
 * 200, content-type `text/html`. The browser then tries to parse
 * the HTML as a JS module and throws:
 *
 *   TypeError: 'text/html' is not a valid JavaScript MIME type.
 *
 * (See Sentry ORBRASIL-FRONTEND-2.) This is independent of the user
 * — it strikes anyone who held a tab open across a deploy.
 *
 * The fix
 * -------
 * When the lazy factory rejects with one of the known stale-chunk
 * signatures, force a full page reload. The new HTML references the
 * new chunk hashes, the import succeeds, and the user lands on the
 * route they were trying to visit.
 *
 * A sessionStorage flag guards against infinite reload loops if the
 * problem is *not* a stale chunk (e.g. a real corrupted bundle on
 * R2). If we already reloaded once in this tab, surface the error
 * to the ErrorBoundary instead of looping.
 *
 * Concurrent failures (the Jun 9 regression of ORBRASIL-FRONTEND-2)
 * ----------------------------------------------------------------
 * A spec table fires a dozen icon imports in the same render commit.
 * When the first one detects a stale chunk it sets the guard and
 * calls location.reload() — but the navigation takes a beat to
 * commit, and the sibling imports' failures land in that beat. They
 * used to see the guard already set and rethrow, spamming the
 * ErrorBoundary + Sentry with errors the in-flight reload was about
 * to cure (three console errors in the same second on the Jun 9
 * event). Now a guard younger than RELOAD_GRACE_MS means "a recovery
 * reload is on its way" — those siblings stay silently pending and
 * let the reload supersede them. Only a guard OLDER than the grace
 * window (a reload that didn't cure the problem) surfaces the error.
 */

const RELOAD_KEY = "orbrasil-stale-chunk-reloaded";

/**
 * How long a just-triggered recovery reload is considered in flight.
 * Within this window, further stale-chunk failures are siblings of
 * the one that triggered the reload — suppress them. Past it, the
 * reload evidently didn't fix things (truly broken bundle, HTML
 * cached by a broken intermediary) — let the ErrorBoundary have it.
 */
const RELOAD_GRACE_MS = 15_000;

/**
 * In-memory mirror of the sessionStorage guard. sessionStorage can
 * throw (Safari private windows, lockdown mode, sandboxed iframes);
 * without the mirror a storage failure would let every concurrent
 * stale-chunk failure trigger its own reload. The mirror lives until
 * the reload navigation commits, which is exactly the window it
 * needs to cover.
 */
let memoryGuard: number | null = null;

/**
 * Test seam: jsdom's window.location is unforgeable, so unit tests
 * can't spy on reload() directly. Not part of the public API.
 */
export const staleChunkInternals = {
  reload: (): void => window.location.reload(),
};

/**
 * The four common ways a stale-chunk error surfaces across Chrome /
 * Safari / Firefox. Any of these strings means "the JS file the bundle
 * expected is no longer at that URL."
 *
 * Exported because lib/sentry.ts filters on the same set. That list
 * used to be a hand-copied duplicate with a "keep in sync" comment,
 * which is how it came to silently drop the very errors that broke a
 * page (see isStaleChunkMessage's callers there).
 */
export const STALE_CHUNK_PATTERNS: readonly RegExp[] = [
  /not a valid JavaScript MIME type/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \d+ failed/i,
];

/** Does this message read as a stale chunk? */
export function isStaleChunkMessage(message: string): boolean {
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message));
}

export function isStaleChunkError(err: unknown): boolean {
  return isStaleChunkMessage(err instanceof Error ? err.message : String(err));
}

function readGuard(): number | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (raw !== null) return Number(raw);
  } catch {
    // storage unavailable — fall through to the in-memory mirror
  }
  return memoryGuard;
}

function writeGuard(ts: number): void {
  memoryGuard = ts;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(ts));
  } catch {
    // storage unavailable — the in-memory mirror carries the guard
  }
}

/**
 * Shared recovery path for every stale-chunk catch in this module.
 *
 * - First stale failure in this tab: set the guard, reload, and hold
 *   the promise pending forever so React renders nothing (the reload
 *   supersedes whatever was suspending).
 * - Failure while a recovery reload is in flight (guard younger than
 *   RELOAD_GRACE_MS): also hold pending — silently. Rethrowing here
 *   is what produced the ErrorBoundary + Sentry noise.
 * - Guard older than the grace window: the reload didn't cure it —
 *   rethrow to the ErrorBoundary.
 * - Not a stale-chunk error, or SSR: rethrow untouched.
 */
function recoverOrRethrow<T>(err: unknown): Promise<T> {
  if (!isStaleChunkError(err) || typeof window === "undefined") throw err;

  const ts = readGuard();
  if (ts === null) {
    // Set the guard *before* reloading so concurrent failures (and
    // the post-reload tab) know recovery is already underway.
    writeGuard(Date.now());
    staleChunkInternals.reload();
    // Hold the Promise pending forever so React doesn't try to
    // render anything while the reload is in flight.
    return new Promise<T>(() => undefined as unknown as T);
  }
  if (Date.now() - ts < RELOAD_GRACE_MS) {
    // Sibling failure racing the in-flight reload — stay silent.
    return new Promise<T>(() => undefined as unknown as T);
  }
  // We already reloaded once in this tab and the problem persists —
  // surface to the ErrorBoundary.
  throw err;
}

/**
 * Preloadable lazy component type. Same shape as a normal React
 * component plus a `.preload()` method that callers can use to
 * pre-warm the module before any render. When the module is
 * preloaded, the component renders synchronously on first read —
 * no Suspense fallback flicker, no hydration mismatch on routes
 * that the prerender baked into the SSR HTML.
 *
 * This deviates from `React.lazy` in that the component is a plain
 * function rather than a `LazyExoticComponent`. Both integrate with
 * Suspense by throwing a Promise on first read; the difference is
 * that this version checks a cached module reference *before*
 * throwing, so a preloaded module bypasses the suspend altogether.
 */
type Preloadable<P> = ((props: P) => ReactElement | null) & {
  preload: () => Promise<unknown>;
};

// The props type is DERIVED from the component the factory resolves to, not
// a separate parameter: only `T` is inferable at a call site like
// `lazyWithReload(() => import("./pages/ProductDetail"))`, so a standalone
// `P` fell back to `unknown` and rejected every page that takes real props.
// `ComponentType<any>` is the standard constraint for "any component" — a
// narrower one can't accept both prop-less and prop-taking pages.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): Preloadable<ComponentProps<T>> {
  // Cached module reference. Set the moment the dynamic import
  // resolves; first read after that returns the component
  // synchronously instead of suspending.
  let resolved: T | null = null;
  // Cached promise so concurrent reads + a preload() call all share
  // a single fetch.
  let loading: Promise<T> | null = null;

  const load = (): Promise<T> => {
    if (loading) return loading;
    loading = factory()
      .then((mod) => {
        resolved = mod.default;
        return resolved;
      })
      .catch((err) => recoverOrRethrow<T>(err));
    return loading;
  };

  const LazyComponent = (props: unknown): ReactElement | null => {
    if (resolved) {
      // Cache hit — render synchronously.
      return createElement(resolved as ComponentType<unknown>, props as Record<string, unknown>);
    }
    // First read with no preload — fall back to Suspense (throw the
    // pending Promise; the wrapping <Suspense> shows its fallback).
    throw load();
  };
  // React DevTools shows this name in the component tree.
  LazyComponent.displayName = "LazyWithReload";
  // Attach preload as a static method so callers (main.tsx) can
  // pre-warm the module before hydrateRoot fires. Cast through
  // `unknown` because TS can't see how the component type widens
  // to a Preloadable once we attach the static method.
  const withPreload = LazyComponent as unknown as Preloadable<unknown>;
  withPreload.preload = load;
  return withPreload;
}

/**
 * Clear the stale-chunk reload guard. Called once on app boot from
 * main.tsx so that a fresh navigation gets a fresh chance to recover
 * from a future deploy. The guard only matters within a single
 * reload cycle — once the new bundle hydrates successfully, we want
 * the next stale chunk in this tab to trigger another reload.
 */
export function clearStaleChunkGuard(): void {
  memoryGuard = null;
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // sessionStorage can throw in private mode / iframes. Non-fatal.
  }
}

/**
 * Wraps a `() => Promise<T>` factory in the same stale-chunk
 * recovery `lazyWithReload` uses, but for places where the factory
 * is consumed by something OTHER than React.lazy at the route
 * level — e.g. SpecIcon's per-icon loader cache, or HeroAccent's
 * `lazy(import('Model3DViewer'))` wrapper.
 *
 * Without this, a stale-deploy chunk-fetch failure surfaces in the
 * nearest ErrorBoundary instead of triggering a guarded reload.
 * Sentry #7469017596 (May 8 deploy 18d8d38) was a per-icon chunk
 * failure caught by ErrorBoundary because SpecIcon's `lazy(loader)`
 * bypassed the recovery codepath the route table already had.
 */
export function withStaleChunkRecovery<T>(
  factory: () => Promise<T>,
): () => Promise<T> {
  return () => factory().catch((err) => recoverOrRethrow<T>(err));
}
